import React, { useState, useEffect } from 'react';
import { signIn, confirmSignIn, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Forgot password states
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [resetNewPassword, setResetNewPassword] = useState('');

    useEffect(() => {
        if (isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await signIn({ username: email, password });
            
            // Check if user needs to change password (first time login)
            if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                setIsChangingPassword(true);
                setLoading(false);
                return;
            }
            
            console.log('Login successful:', result);
            onLoginSuccess();
            onClose();
            
        } catch (err) {
            console.error('Login error:', err);
            setError(err.message || 'Login failed. Please check your credentials.');
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            await confirmSignIn({ challengeResponse: newPassword });
            
            console.log('Password changed successfully');
            onLoginSuccess();
            resetForm();
            onClose();
            
        } catch (err) {
            console.error('Password change error:', err);
            setError(err.message || 'Failed to change password');
            setLoading(false);
        }
    };

    const handleSendResetCode = async () => {
        setLoading(true);
        setError('');
        
        try {
            await resetPassword({ username: resetEmail });
            setCodeSent(true);
        } catch (err) {
            setError(err.message || 'Failed to send reset code');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        setLoading(true);
        setError('');
        
        try {
            await confirmResetPassword({
                username: resetEmail,
                confirmationCode: resetCode,
                newPassword: resetNewPassword
            });
            setShowForgotPassword(false);
            setCodeSent(false);
            setError('');
            alert('Password reset successfully! Please log in with your new password.');
        } catch (err) {
            setError(err.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setEmail('');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setIsChangingPassword(false);
        setShowPassword(false);
        setError('');
        setLoading(false);
    };

    const resetForgotPasswordForm = () => {
        setShowForgotPassword(false);
        setCodeSent(false);
        setResetEmail('');
        setResetCode('');
        setResetNewPassword('');
        setError('');
        setLoading(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="login-modal-overlay" onClick={handleClose}>
                <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="login-modal-header">
                        <h2>{isChangingPassword ? 'Change Password' : 'Admin Login'}</h2>
                        <button className="close-button" onClick={handleClose}>&times;</button>
                    </div>

                    {!isChangingPassword ? (
                        <form onSubmit={handleLogin} className="login-form">
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="off"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        autoComplete="off"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <button type="submit" className="login-button" disabled={loading}>
                                {loading ? 'Logging in...' : 'Login'}
                            </button>

                            <div className="forgot-password-link">
                                <button
                                    type="button"
                                    className="forgot-password-button"
                                    onClick={() => setShowForgotPassword(true)}
                                >
                                    Forgot password?
                                </button>
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={handlePasswordChange} className="login-form">
                            <p className="info-message">Please change your temporary password</p>
                            
                            <div className="form-group">
                                <label htmlFor="newPassword">New Password</label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                />
                            </div>

                            {error && <div className="error-message">{error}</div>}

                            <button type="submit" className="login-button" disabled={loading}>
                                {loading ? 'Changing...' : 'Change Password'}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="forgot-password-overlay">
                    <div className="forgot-password-content">
                        <h3>Reset Password</h3>
                        
                        {!codeSent ? (
                            <>
                                <p>Enter your email to receive a reset code</p>
                                <input
                                    type="email"
                                    className="forgot-password-input"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    placeholder="Email"
                                />
                                <button
                                    className="forgot-password-submit"
                                    onClick={handleSendResetCode}
                                    disabled={loading}
                                >
                                    {loading ? 'Sending...' : 'Send Reset Code'}
                                </button>
                            </>
                        ) : (
                            <>
                                <p>Enter the code sent to {resetEmail}</p>
                                <input
                                    type="text"
                                    className="forgot-password-input"
                                    value={resetCode}
                                    onChange={(e) => setResetCode(e.target.value)}
                                    placeholder="Reset code"
                                />
                                <input
                                    type="password"
                                    className="forgot-password-input"
                                    value={resetNewPassword}
                                    onChange={(e) => setResetNewPassword(e.target.value)}
                                    placeholder="New password"
                                />
                                <button
                                    className="forgot-password-submit"
                                    onClick={handleResetPassword}
                                    disabled={loading}
                                >
                                    {loading ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </>
                        )}
                        
                        <button
                            className="forgot-password-cancel"
                            onClick={resetForgotPasswordForm}
                        >
                            Cancel
                        </button>
                        
                        {error && <div className="error-message" style={{marginTop: '10px'}}>{error}</div>}
                    </div>
                </div>
            )}
        </>
    );
};

export default LoginModal;